import ArrowBackIosOutlinedIcon from "@mui/icons-material/ArrowBackIosOutlined";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "@hello-pangea/dnd";
import { toast } from "react-toastify";
import { ColunaPreferencia } from "../../../hooks/usePreferenciasColunas";

interface ModalProps {
  aberto: boolean;
  onFechar: () => void;
  // Todas as colunas, na ordem atual, com a marcação de ocultas
  colunas: ColunaPreferencia[];
  onSalvar: (config: {
    colunas: ColunaPreferencia[];
    itensPorPagina: number;
  }) => void;
  onResetar: () => void;
  itensPorPaginaAtual?: number;
  itensPorPaginaPadrao?: number;
  cols?: number;
  autoBreakpoints?: [number, number];
  maxCols?: 1 | 2 | 3;
}

// Modal de preferências de exibição do grid (mesmo padrão do XC WMS)
const ModalPreferenciasColunas: React.FC<ModalProps> = ({
  aberto,
  onFechar,
  colunas,
  onSalvar,
  onResetar,
  itensPorPaginaAtual = 10,
  itensPorPaginaPadrao = 10,
  autoBreakpoints = [10, 15],
  cols,
  maxCols = 3,
}) => {
  const theme = useTheme();

  const [selecionadas, setSelecionadas] = useState<ColunaPreferencia[]>([]);
  const [itensPorPagina, setItensPorPaginaLocal] =
    useState<number>(itensPorPaginaAtual);

  useEffect(() => {
    if (!aberto) return;

    setSelecionadas(colunas.map((coluna) => ({ ...coluna })));
    setItensPorPaginaLocal(itensPorPaginaAtual);
  }, [aberto, colunas, itensPorPaginaAtual]);

  const handleToggle = (key: string) => {
    setSelecionadas((prev) =>
      prev.map((coluna) =>
        coluna.key === key ? { ...coluna, hidden: !coluna.hidden } : coluna,
      ),
    );
  };

  const total = selecionadas.length || colunas.length;

  const COLS = useMemo(() => {
    if (cols && cols > 0) return Math.min(cols, maxCols);

    const [max1, max2] = autoBreakpoints;

    if (total <= max1) return 1;
    if (total <= max2) return Math.min(2, maxCols);

    return Math.min(3, maxCols);
  }, [cols, maxCols, autoBreakpoints, total]);

  const colId = (c: number) => `col-${c}`;
  const parseCol = (id: string) => Number(id.replace("col-", ""));

  const rowsInCol = (col: number, t: number, c: number) => {
    const base = Math.floor(t / c);
    const extra = t % c;

    return col < extra ? base + 1 : base;
  };

  const indexToColRow = (idx: number, t: number, c: number) => {
    let col = 0;
    let offset = idx;

    while (col < c) {
      const size = rowsInCol(col, t, c);

      if (offset < size) return { col, row: offset };

      offset -= size;
      col++;
    }

    const lastCol = Math.max(c - 1, 0);

    return {
      col: lastCol,
      row: Math.max(rowsInCol(lastCol, t, c) - 1, 0),
    };
  };

  const colRowToIndex = (col: number, row: number, t: number, c: number) => {
    let idx = 0;

    for (let i = 0; i < col; i++) {
      idx += rowsInCol(i, t, c);
    }

    return idx + row;
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (!destination) return;

    const sourceCol = parseCol(source.droppableId);
    const destCol = parseCol(destination.droppableId);

    const sourceGlobalIndex = colRowToIndex(
      sourceCol,
      source.index,
      total,
      COLS,
    );

    const destGlobalIndex = colRowToIndex(
      destCol,
      destination.index,
      total - 1,
      COLS,
    );

    setSelecionadas((prev) => {
      const items = [...prev];
      const from = Math.min(Math.max(sourceGlobalIndex, 0), items.length - 1);
      const [moved] = items.splice(from, 1);

      if (!moved) return prev;

      const to = Math.max(0, Math.min(destGlobalIndex, items.length));
      items.splice(to, 0, moved);

      return items;
    });
  };

  const focarInputItensPorPagina = () => {
    setTimeout(() => {
      const input = document.querySelector<HTMLInputElement>(
        'input[name="itensPorPagina"]',
      );

      if (input) input.focus();
    }, 300);
  };

  const validarItensPorPagina = () => {
    if (isNaN(itensPorPagina)) {
      toast.error("A quantidade de itens por página deve ser um número válido.");
      focarInputItensPorPagina();
      return false;
    }

    if (!isFinite(itensPorPagina)) {
      toast.error("A quantidade de itens por página deve ser um número finito.");
      focarInputItensPorPagina();
      return false;
    }

    if (!Number.isInteger(itensPorPagina)) {
      toast.error("A quantidade de itens por página deve ser um número inteiro.");
      focarInputItensPorPagina();
      return false;
    }

    if (!itensPorPagina) {
      toast.error("A quantidade de itens por página é obrigatória.");
      focarInputItensPorPagina();
      return false;
    }

    if (itensPorPagina < 1) {
      toast.error("A quantidade de itens por página deve ser no mínimo 1.");
      focarInputItensPorPagina();
      return false;
    }

    if (itensPorPagina > 100) {
      toast.error("A quantidade de itens por página deve ser no máximo 100.");
      focarInputItensPorPagina();
      return false;
    }

    return true;
  };

  const handleSalvar = () => {
    if (!validarItensPorPagina()) return;

    if (!selecionadas.some((coluna) => !coluna.hidden)) {
      toast.error("Selecione ao menos uma coluna.");
      return;
    }

    onSalvar({
      colunas: selecionadas,
      itensPorPagina,
    });
  };

  const handleResetar = () => {
    setSelecionadas(
      selecionadas.map((coluna) => ({
        ...coluna,
        hidden: false,
      })),
    );
    setItensPorPaginaLocal(itensPorPaginaPadrao);
    onResetar();
  };

  return (
    <Dialog
      open={aberto}
      onClose={onFechar}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          resize: "vertical",
          overflow: "hidden",
          minHeight: 300,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        },
      }}
    >
      <DialogTitle>
        Preferências de Exibição
        <Divider sx={{ borderColor: theme.palette.divider }} />
      </DialogTitle>

      <DialogTitle variant="body2" sx={{ mt: -4 }}>
        Selecione as colunas, arraste para ordenar e defina a quantidade de
        registros por página:
        <Divider sx={{ borderColor: theme.palette.divider }} />
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflowY: "auto", pt: 1, pb: 1, mt: -4 }}>
        <Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Itens por página"
              variant="outlined"
              fullWidth
              size="small"
              name="itensPorPagina"
              value={itensPorPagina}
              type="number"
              inputProps={{ min: 1 }}
              onChange={(e) => setItensPorPaginaLocal(Number(e.target.value))}
            />
          </Grid>
        </Grid>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${Math.max(COLS, 1)}, 1fr)`,
              gap: 1,
              alignItems: "start",
            }}
          >
            {Array.from({ length: Math.max(COLS, 1) }).map((_, col) => (
              <Droppable
                droppableId={colId(col)}
                key={col}
                direction="vertical"
              >
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      minHeight: 20,
                    }}
                  >
                    {selecionadas.map((item, idx) => {
                      const { col: itemCol, row } = indexToColRow(
                        idx,
                        selecionadas.length,
                        Math.max(COLS, 1),
                      );

                      if (itemCol !== col) return null;

                      return (
                        <Draggable
                          key={item.key}
                          draggableId={item.key}
                          index={row}
                        >
                          {(provided) => (
                            <Box
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              sx={{
                                backgroundColor: theme.palette.action.selected,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: "4px",
                                px: 1,
                                py: 0.5,
                                display: "flex",
                                alignItems: "center",
                                minHeight: 36,
                              }}
                              style={provided.draggableProps.style}
                            >
                              <DragHandleIcon
                                fontSize="small"
                                style={{ marginRight: 8 }}
                              />

                              <Checkbox
                                checked={!item.hidden}
                                onChange={() => handleToggle(item.key)}
                                sx={{
                                  mr: 1,
                                  p: 0.25,
                                  color: theme.palette.warning.main,
                                  "&.Mui-checked": {
                                    color: theme.palette.warning.main,
                                  },
                                }}
                              />

                              <Typography
                                variant="body2"
                                noWrap
                                title={
                                  typeof item.label === "string"
                                    ? item.label
                                    : undefined
                                }
                              >
                                {item.label}
                              </Typography>
                            </Box>
                          )}
                        </Draggable>
                      );
                    })}

                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            ))}
          </Box>
        </DragDropContext>
      </DialogContent>

      <Divider sx={{ borderColor: theme.palette.divider, mx: 3 }} />

      <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 1 }}>
        <Button
          variant="contained"
          color="warning"
          onClick={onFechar}
          startIcon={<ArrowBackIosOutlinedIcon />}
          size="small"
        >
          Voltar
        </Button>

        <Button
          variant="contained"
          color="inherit"
          startIcon={<RestartAltIcon />}
          size="small"
          onClick={handleResetar}
        >
          Reiniciar
        </Button>

        <Button
          variant="contained"
          color="success"
          startIcon={<SaveOutlinedIcon />}
          size="small"
          onClick={handleSalvar}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalPreferenciasColunas;
